import React from 'react';

export interface SustainabilityInsightFactor {
  name: string;
  value: string;
  detail: string;
  tone: 'good' | 'watch' | 'neutral';
  direction?: 'up' | 'down' | 'none';
}

export interface SustainabilityInsight {
  summary: React.ReactNode;
  factors: SustainabilityInsightFactor[];
  suggestion?: string | null;
}

export interface SustainabilityAiInsightProps {
  insight: SustainabilityInsight;
  title?: string;
  className?: string;
}

const AI_INSIGHT_ICON_PATH = [
  'M12 2.5c.35 0 .66.23.76.57l1.28 4.4a5.2 5.2 0 0 0 3.55 3.55',
  'l4.4 1.28a.79.79 0 0 1 0 1.52l-4.4 1.28a5.2 5.2 0 0 0-3.55 3.55',
  'l-1.28 4.4a.79.79 0 0 1-1.52 0l-1.28-4.4a5.2 5.2 0 0 0-3.55-3.55',
  'l-4.4-1.28a.79.79 0 0 1 0-1.52l4.4-1.28a5.2 5.2 0 0 0 3.55-3.55',
  'l1.28-4.4c.1-.34.41-.57.76-.57Z'
].join('');

const SustainabilityAiInsight: React.FC<SustainabilityAiInsightProps> = ({
  insight,
  title = 'AI-generated insight',
  className = ''
}) => (
  <aside
    className={`yoySustainabilityInsight ${className}`.trim()}
    aria-label={title}
  >
    <div className="yoySustainabilityInsightHeader">
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d={AI_INSIGHT_ICON_PATH} />
      </svg>
      <span>{title}</span>
    </div>

    <div className="yoySustainabilityInsightSummary">{insight.summary}</div>

    {insight.factors.length > 0 && (
      <div className="yoySustainabilityFactors">
        {insight.factors.map((factor) => {
          const marker =
            factor.direction === 'up'
              ? '↑'
              : factor.direction === 'down'
                ? '↓'
                : '•';

          return (
            <div className="yoySustainabilityFactor" key={factor.name}>
              <span className={`yoySustainabilityFactorIcon ${factor.tone}`}>
                {marker}
              </span>
              <span className="yoySustainabilityFactorText">
                <strong>{factor.name}</strong> — {factor.detail}
              </span>
              {factor.value ? (
                <strong className="yoySustainabilityFactorValue">
                  {factor.value}
                </strong>
              ) : null}
            </div>
          );
        })}
      </div>
    )}

    {insight.suggestion ? (
      <div className="yoySustainabilityInsightSummary">{insight.suggestion}</div>
    ) : null}
  </aside>
);

export default SustainabilityAiInsight;
